import React, { useEffect, useState, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { getAvailableSeats, getSubUsers, addSubUser, deleteSubUser, updateSubUser, resendInviteEmail } from '../../api/studioOwnerApi';
import { userInfoAtom } from '../../data/atoms/utilsAtoms';
import Loader from '../LoderComponent';
import Toast from '../../utils/toastManager';
import { Delete, Edit, Person2Outlined } from '@mui/icons-material';
import ConfirmationModal from '../ConfirmationModal';
import EditSubUserModal from '../EditSubuserModal';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const SubUserAdd = ({ subscriptionData, setSeatsData }) => {
	const existingAccountMessage =
		'Unable to add this user as they currently have an account with Godfrey Method On-Demand. Please reach out to our Support team to resolve this issue.';

	const userData = useAtomValue(userInfoAtom);
	const [subUserData, setSubUserData] = useState([]);
	const [rowIndex, setRowIndex] = useState(null);
	const [activeInput, setActiveInput] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);
	const [availableSeats, setAvailableSeats] = useState();
	const [newSubUserData, setNewSubUserData] = useState({
		firstname: '',
		lastname: '',
		email: ''
	});
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [userToEdit, setUserToEdit] = useState(null);
	const [resendingUserId, setResendingUserId] = useState(null);
	const labels = ['Name', 'Status', 'Date Added', 'Actions'];

	const getAddMemberErrorMessage = error => {
		const responseData = error?.response?.data;
		const rawMessage =
			responseData?.message ||
			responseData?.error?.message ||
			responseData?.error ||
			error?.message;

		if (error?.response?.status === 409) {
			return rawMessage || existingAccountMessage;
		}

		if (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('currently have an account')) {
			return existingAccountMessage;
		}

		return rawMessage || 'Failed to add user';
	};

	const fetchData = async () => {
		try {
			const seatsResponse = await getAvailableSeats(userData?.token);
			
			const seats = seatsResponse.data.data.requiredSeats;

			const subUsersResponse = await getSubUsers(userData?.token);
			
			const existingSubUsers = subUsersResponse.data.data.subUsers;
			setAvailableSeats(seats - existingSubUsers.length);
			setSeatsData(seats);

			const emptySubUsers = Array.from({ length: seats - existingSubUsers.length }, () => ({
				firstname: '',
				lastname: '',
				email: '',
				createdAt: ''
			}));

			setSubUserData([...existingSubUsers, ...emptySubUsers]);
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch data';
			Toast.error(errorMessage);
		}
	};

	const openEditModal = user => {
		setUserToEdit(user);
		setIsEditModalOpen(true);
	};

	useEffect(() => {
		fetchData();
	}, [userData[0]?.token]);

	const handleSave = async () => {
		setLoading(true);
		try {
			const response = await addSubUser(userData?.token, newSubUserData);
			if (response.status === 200) {
				Toast.success('User added successfully');
				fetchData();
			}
		} catch (error) {
			const errorMessage = getAddMemberErrorMessage(error);
			Toast.error(errorMessage);

			const updatedSubUserData = [...subUserData];
			setSubUserData(updatedSubUserData);
		} finally {
			setActiveInput(false);

			newSubUserData.firstname = '';
			newSubUserData.lastname = '';
			newSubUserData.email = '';
			setLoading(false);
		}
	};

	const handleEdit = async updatedUserData => {
		try {
			const response = await updateSubUser(userData?.token, updatedUserData);

			if (response.status === 200) {
				Toast.success('User updated successfully');
				fetchData();
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to update user';
			Toast.error(errorMessage);
		} finally {
			setIsEditModalOpen(false);
			setUserToEdit(null);
		}
	};

	const handleDeleteClick = data => {
		setUserToDelete(data);
		setIsModalOpen(true);
	};

	const handleDeleteConfirm = async () => {
		try {
			const response = await deleteSubUser(userData?.token, userToDelete._id);
			if (response.status === 200) {
				Toast.success('User Deleted Successfully');
				fetchData();
			}
			if (response.status === 201) {
				Toast.error('Please Login Again');
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to delete user';
			Toast.error(errorMessage);
		} finally {
			setIsModalOpen(false);
			setUserToDelete(null);
		}
	};

	const handleResendEmail = async (user) => {
		setResendingUserId(user._id);
		try {
			const response = await resendInviteEmail(userData?.token, user._id);
			if (response.status === 200) {
				Toast.success('Invite email sent successfully to ' + (user.secondaryEmail || user.displayEmail || user.email));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to resend email';
			Toast.error(errorMessage);
		} finally {
			setResendingUserId(null);
		}
	};

	const formatName = (firstname, lastname) => {
		return firstname && lastname ? `${firstname} ${lastname}` : 'Available Seat';
	};

	const formatDate = dateString => {
		const options = { year: 'numeric', month: 'long', day: 'numeric' };
		return new Date(dateString).toLocaleDateString(undefined, options);
	};

	return (
		<div className="sub-user-add-table">
			{loading && <Loader />}
			{subscriptionData?.subscriptionStatus === 'active' ? (
				<>
					<div className="add_subuser_form">
						<div className="add_subuser_form_head">
							<div className="form_head">
								<h4>ASSIGN SEAT .</h4>
							</div>
							{availableSeats > 0 ? (
								<span className="available_seats">{availableSeats} seats left</span>
							) : (
								<span className="available_seats">No seats left</span>
							)}
						</div>
						<div className="add_subuser_form_container">
							<div className="add_subuser_form_body">
								<div className="c-my-acc_form_item_subuser">
									<input
										value={newSubUserData.firstname}
										onChange={e =>
											setNewSubUserData({
												...newSubUserData,
												firstname: e.target.value
											})
										}
										type="text"
										placeholder="First Name"
										className="first_name"
										disabled={availableSeats <= 0}
									/>
								</div>
								<div className="c-my-acc_form_item_subuser">
									<input
										value={newSubUserData.lastname}
										onChange={e =>
											setNewSubUserData({
												...newSubUserData,
												lastname: e.target.value
											})
										}
										type="text"
										placeholder="Last Name"
										className="last_name"
										disabled={availableSeats <= 0}
									/>
								</div>
								<div className="c-my-acc_form_item_subuser">
									<input
										value={newSubUserData.email}
										onChange={e =>
											setNewSubUserData({
												...newSubUserData,
												email: e.target.value
											})
										}
										type="text"
										placeholder="Email"
										className="email"
										disabled={availableSeats <= 0}
									/>
								</div>
							</div>
							{availableSeats > 0 ? (
								<button className="c-form_my-btn" onClick={handleSave}>
									ADD MEMBER
								</button>
							) : (
								<button className="no-seats-left-button" disabled={availableSeats <= 0}>
									NO SEATS LEFT
								</button>
							)}
						</div>
					</div>
					<div className="sub_user_add_table_container">
						<TableContainer sx={{ paddingRight: '10px', paddingLeft: '10px' }}>
							<Table sx={{ minWidth: 650 }} aria-label="simple table">
								<TableHead sx={{ padding: 3 }}>
									<TableRow>
										{labels.map((label, index) => (
											<TableCell
												sx={{
													padding: '0px',
													paddingTop: '10px',
													paddingBottom: '10px',
													paddingLeft: '10px',
													textAlign: 'left',
													justifySelf: 'start'
												}}
												key={index}
											>
												<div className="table-label">
													<h4>{label}</h4>
												</div>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{subUserData.map((data, index) => (
										<TableRow
											key={index}
											ref={rowIndex === index ? rowRef : null}
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell
												scope="row"
												sx={{
													color: 'white',
													paddingLeft: '10px',
													display: 'flex',
													alignItems: 'center'
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center'
													}}
												>
													<Person2Outlined
														style={{
															border: '1px solid #e5',
															borderRadius: '50%',
															width: '3rem',
															height: '3rem',
															padding: '2px',
															backgroundColor: data.firstname && data.lastname ? '#3cb4e4' : 'gray'
														}}
													/>

													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															fontSize: '14px',
															justifyContent: 'center',
															marginLeft: '20px'
														}}
													>
														{formatName(data.firstname, data.lastname)}
														{(data.secondaryEmail || data.displayEmail || data.email) && (
															<p style={{ marginTop: '5px', color: 'gray' }}>
																{data.secondaryEmail || data.displayEmail || data.email}
															</p>
														)}
													</div>
												</div>
											</TableCell>
											<TableCell sx={{ color: 'white' }}>
												{data.createdAt ? (
													<>
														{!data.password ? (
															<button
																onClick={() => handleResendEmail(data)}
																className="c-form_my-btn"
																disabled={resendingUserId === data._id}
																style={{
																	padding: '4px 8px',
																	fontSize: '11px',
																	whiteSpace: 'nowrap',
																	opacity: resendingUserId === data._id ? 0.6 : 1
																}}
															>
																{resendingUserId === data._id ? 'SENDING...' : 'RESEND INVITE'}
															</button>
														) : (
															<span style={{ color: '#3cb4e4' }}>Invite Accepted</span>
														)}
													</>
												) : (
													<div style={{ textAlign: 'left' }}>-</div>
												)}
											</TableCell>
											<TableCell sx={{ color: 'white' }}>
												{data.createdAt ? (
													<div style={{ textAlign: 'left' }}>{formatDate(data.createdAt)}</div>
												) : (
													<div style={{ textAlign: 'left' }}>EMPTY SEAT</div>
												)}
											</TableCell>
											{data.userType === 'studio-owner' ? (
												<TableCell></TableCell>
											) : (
												<TableCell>
													{data.firstname && data.lastname && (data.secondaryEmail || data.displayEmail || data.email) ? (
														<>
															<Edit
																onClick={() => openEditModal(data)}
																sx={{
																	color: 'white',
																	cursor: 'pointer',
																	marginRight: '10px'
																}}
															/>
															<Delete
																onClick={() => handleDeleteClick(data)}
																sx={{
																	color: 'white',
																	cursor: 'pointer'
																}}
															/>
														</>
													) : (
														<>
															<Edit
																sx={{
																	color: 'gray',
																	cursor: 'not-allowed',
																	marginRight: '10px'
																}}
																disabled
															/>
															<Delete
																sx={{
																	color: 'gray',
																	cursor: 'not-allowed'
																}}
																disabled
															/>
														</>
													)}
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</div>

					{userToEdit && (
						<EditSubUserModal
							open={isEditModalOpen}
							handleClose={() => setIsEditModalOpen(false)}
							userData={userToEdit}
							onSave={handleEdit}
						/>
					)}

					<ConfirmationModal
						isOpen={isModalOpen}
						onClose={() => setIsModalOpen(false)}
						onConfirm={handleDeleteConfirm}
					/>
				</>
			) : (
				<div className="activate-subscription">* Please activate any of the studio plans</div>
			)}
		</div>
	);
};

export default SubUserAdd;
